import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const Resources: React.FC = () => {
  const [activeTab, setActiveTab] = useState('guide');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-blue-600 to-teal-500 text-white">
        <div className="container mx-auto text-center max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-100">
              Indian Postal Code Resources & Guides
            </h1>
            <p className="text-xl md:text-2xl mb-12 text-blue-50">
              Comprehensive guides and resources for understanding India's postal system
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Tab Navigation */}
        <div className="flex justify-center mb-8">
          <nav className="flex space-x-4 bg-white rounded-lg shadow-sm p-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-md transition-colors duration-200 ${
                  activeTab === tab.id
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* User Guide Section */}
        {activeTab === 'guide' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-md p-8"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">User Guide</h2>
            <div className="space-y-8">
              {userGuides.map((guide) => (
                <div key={guide.title} className="border-b border-gray-200 pb-6 last:border-0">
                  <h3 className="text-xl font-semibold text-purple-900 mb-4">{guide.title}</h3>
                  <p className="text-gray-600 mb-4">{guide.description}</p>
                  <ul className="list-disc pl-6 space-y-2">
                    {guide.points.map((point, i) => (
                      <li key={i} className="text-gray-600">{point}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Educational Resources Section */}
        {activeTab === 'learn' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {resources.map((resource) => (
              <div key={resource.title} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="p-6">
                  <div className="text-4xl mb-4">{resource.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {resource.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{resource.description}</p>
                  <ul className="space-y-2">
                    {resource.points.map((point, i) => (
                      <li key={i} className="flex items-start">
                        <span className="text-purple-600 mr-2">•</span>
                        <span className="text-gray-600">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </motion.div>
        )}

        {/* API Documentation Section */}
        {activeTab === 'api' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-md p-8"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">API Documentation</h2>
            <div className="prose max-w-none">
              <p className="text-lg text-gray-600 mb-6">
                Access India's most comprehensive pincode database through our REST API. Perfect for e-commerce, logistics, and address verification systems.
              </p>
              
              <div className="space-y-8">
                {apiEndpoints.map((endpoint) => (
                  <ApiEndpoint key={endpoint.endpoint} {...endpoint} />
                ))}
              </div>

              <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Getting Started</h3>
                <ol className="list-decimal pl-6 space-y-2">
                  <li>Sign up for an API key</li>
                  <li>Include your API key in the request headers</li>
                  <li>Make requests to our endpoints</li>
                  <li>Handle the JSON response in your application</li>
                </ol>
              </div>
            </div>
          </motion.div>
        )}

        {/* Support Section */}
        {activeTab === 'support' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-md p-8"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Support Center</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-purple-900 mb-4">Contact Us</h3>
                <ul className="space-y-4">
                  {contactInfo.map((info) => (
                    <li key={info.label} className="flex items-center text-gray-600">
                      <span className="mr-2">{info.icon}</span>
                      <span>{info.label}: {info.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-purple-900 mb-4">Quick Links</h3>
                <ul className="space-y-2">
                  {quickLinks.map((link) => (
                    <li key={link.label}>
                      <Link
                        to={link.href}
                        className="text-purple-600 hover:text-purple-800 transition-colors duration-200"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

const ApiEndpoint: React.FC<{
  method: string;
  endpoint: string;
  description: string;
  example: string;
}> = ({ method, endpoint, description, example }) => (
  <div className="border border-gray-200 rounded-lg p-4">
    <div className="flex items-center mb-2">
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
        method === 'GET' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
      }`}>
        {method}
      </span>
      <code className="ml-3 text-sm font-mono text-gray-900">{endpoint}</code>
    </div>
    <p className="text-gray-600 mb-2">{description}</p>
    <div className="bg-gray-50 rounded p-3">
      <code className="text-sm font-mono text-gray-700">Example: {example}</code>
    </div>
  </div>
);

const tabs = [
  { id: 'guide', label: 'User Guide' },
  { id: 'learn', label: 'Learn' },
  { id: 'api', label: 'API Docs' },
  { id: 'support', label: 'Support' },
];

const userGuides = [
  {
    title: 'Getting Started with Pincode Pro',
    description: 'Learn how to effectively use our pincode search and verification tools.',
    points: [
      'Enter a 6-digit pincode or area name in the search box',
      'View detailed information about post offices and delivery areas',
      'Use filters to refine your search results',
      'Save frequently searched locations for quick access',
    ],
  },
  {
    title: 'Advanced Search Techniques',
    description: 'Master our advanced search features for precise results.',
    points: [
      'Use partial area names for broader searches',
      'Filter results by state or district',
      'Search by nearby landmarks or localities',
      'Export search results for your records',
    ],
  },
  {
    title: 'Address Verification',
    description: 'Ensure accurate delivery with proper address formatting.',
    points: [
      'Verify complete postal addresses',
      'Check delivery serviceability',
      'Validate PIN codes for specific areas',
      'Format addresses according to India Post guidelines',
    ],
  },
];

const resources = [
  {
    title: 'Understanding Pincodes',
    icon: '📮',
    description: 'Learn about the Indian postal code system and its structure.',
    points: [
      'Six-digit numeric code system',
      'First digit represents postal region',
      'Second digit indicates sub-region',
      'Last three digits for post office identification',
    ],
  },
  {
    title: 'Address Formatting',
    icon: '✉️',
    description: 'Best practices for writing addresses in India.',
    points: [
      'Name and building number',
      'Street name and locality',
      'City and state',
      'Pincode placement',
    ],
  },
  {
    title: 'Postal Services',
    icon: '🚚',
    description: 'Different types of postal services available.',
    points: [
      'Speed Post',
      'Registered Post',
      'Parcel Services',
      'Money Orders',
    ],
  },
];

const apiEndpoints = [
  {
    method: 'GET',
    endpoint: '/api/v1/pincode/{pincode}',
    description: 'Get detailed information about a specific pincode',
    example: 'https://api.pincodepro.in/v1/pincode/110001',
  },
  {
    method: 'GET',
    endpoint: '/api/v1/search/area/{name}',
    description: 'Search post offices by area name',
    example: 'https://api.pincodepro.in/v1/search/area/Connaught%20Place',
  },
  {
    method: 'GET',
    endpoint: '/api/v1/postoffice/{id}',
    description: 'Get detailed information about a specific post office',
    example: 'https://api.pincodepro.in/v1/postoffice/12345',
  },
];

const contactInfo = [
  { icon: '📧', label: 'Email', value: 'support@pincodepro.com' },
  { icon: '📱', label: 'Phone', value: '+91 (800) 123-4567' },
  { icon: '🕒', label: 'Hours', value: '24/7 Support' },
];

const quickLinks = [
  { label: 'FAQs', href: '/resources#faqs' },
  { label: 'API Documentation', href: '/resources#api' },
  { label: 'User Guide', href: '/resources#guide' },
  { label: 'Contact Support', href: '/resources#support' },
];

export default Resources;
